import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, ListGroup, Row } from "react-bootstrap";
import { collection, getDocs, orderBy, query, where } from "@firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../../../firebase/FirebaseProvider";
import { Location, Session, User } from "../../types/Types";
import { getDistanceFromLatLonInKm } from "../../util/Util";

const ONE_HOUR_MS = 60 * 60 * 1000;
const TEN_MINUTES_MS = 10 * 60 * 1000;
const EIGHTEEN_HOURS_MS = 18 * 60 * 60 * 1000;
const RAPID_TRAVEL_SPEED_KMH = 50;
const SUSPICIOUS_RULES = ["Repeated check-in", "Multiple airports", "Rapid travel", "Quick check-in", "Impossible check-in"];
const STAFF_SKIPPED_RULES = ["Multiple airports", "Impossible check-in"];
const DEFAULT_ANALYSIS_START_DATE = "2015-01-01";

function getDate(value: any): Date | undefined {
    if (!value) {
        return undefined;
    }

    if (value instanceof Date) {
        return value;
    }

    if (typeof value.toDate === "function") {
        return value.toDate();
    }

    if (typeof value.seconds === "number") {
        return new Date(value.seconds * 1000);
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
}

function formatDateTime(value: any): string {
    const date = getDate(value);
    if (!date) {
        return "Unknown";
    }

    return `${date.toLocaleDateString("en-GB")} ${date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit"
    })}`;
}

function formatTime(value: any): string {
    const date = getDate(value);
    if (!date) {
        return "Unknown";
    }

    return date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit"
    });
}

function formatDurationMinutes(valueMs: number | undefined): string {
    if (valueMs === undefined || valueMs < 0) {
        return "Unknown";
    }

    const minutes = Math.floor(valueMs / (60 * 1000));
    return `${minutes}m`;
}

function renderSessionTimeRange(session: Session): React.ReactNode {
    const checkInDate = getDate(session.checkInTime);
    const checkOutDate = getDate(session.checkOutTime);

    if (!checkInDate) {
        return "Unknown";
    }

    const checkInDateText = checkInDate.toLocaleDateString("en-GB");
    const checkInTimeText = formatTime(session.checkInTime);
    const durationText = formatDurationMinutes(getSessionDurationMs(session));

    if (!checkOutDate) {
        return <>{checkInDateText} <b>{checkInTimeText}</b> to Unknown - {durationText}</>;
    }

    const checkOutDateText = checkOutDate.toLocaleDateString("en-GB");
    const checkOutTimeText = formatTime(session.checkOutTime);

    if (checkInDateText === checkOutDateText) {
        return <>{checkInDateText} <b>{checkInTimeText}</b> to <b>{checkOutTimeText}</b> - {durationText}</>;
    }

    return <>{checkInDateText} <b>{checkInTimeText}</b> to {checkOutDateText} <b>{checkOutTimeText}</b> - {durationText}</>;
}

function formatDuration(valueMs: number | undefined): string {
    if (valueMs === undefined || valueMs < 0) {
        return "Unknown time";
    }

    const totalMinutes = Math.floor(valueMs / (60 * 1000));
    const days = Math.floor(totalMinutes / (24 * 60));
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const minutes = totalMinutes % 60;

    if (days > 0) {
        return `${days}d ${hours}h`;
    }

    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }

    return `${minutes}m`;
}

function getDistanceKm(previousLocation: Location | undefined, currentLocation: Location | undefined): number | undefined {
    if (!previousLocation || !currentLocation) {
        return undefined;
    }

    if (!previousLocation.lat || !previousLocation.lng || !currentLocation.lat || !currentLocation.lng) {
        return undefined;
    }

    const distanceKm = getDistanceFromLatLonInKm(
        Number(previousLocation.lat),
        Number(previousLocation.lng),
        Number(currentLocation.lat),
        Number(currentLocation.lng)
    );

    return Number.isFinite(distanceKm) ? distanceKm : undefined;
}

function getSessionDateTime(session: Session): number {
    return getDate(session.checkInTime)?.getTime() || 0;
}

function getSessionDurationMs(session: Session): number | undefined {
    const checkInTime = getDate(session.checkInTime)?.getTime();
    const checkOutTime = getDate(session.checkOutTime)?.getTime();

    if (!checkInTime || !checkOutTime || checkOutTime <= checkInTime) {
        return undefined;
    }

    return checkOutTime - checkInTime;
}

function getRuleBadgeClass(rule: string): string {
    if (rule === "Multiple airports") {
        return "badge badge-info";
    }

    if (rule === "Rapid travel") {
        return "badge badge-warning";
    }

    if (rule === "Quick check-in") {
        return "badge badge-success";
    }

    if (rule === "Impossible check-in") {
        return "badge badge-purple";
    }

    return "badge badge-danger";
}

function getTravelSpeedKmh(previousLocation: Location | undefined, currentLocation: Location | undefined, timeMs: number): number | undefined {
    if (timeMs <= 0) {
        return undefined;
    }

    const distanceKm = getDistanceKm(previousLocation, currentLocation);
    if (distanceKm === undefined) {
        return undefined;
    }

    const timeHours = timeMs / (60 * 60 * 1000);
    const speedKmh = distanceKm / timeHours;

    return Number.isFinite(speedKmh) ? speedKmh : undefined;
}

function isAirsideAirportLocation(location: Location | undefined): boolean {
    if (!location?.airport) {
        return false;
    }

    const locationName = (location.name || "").toLowerCase().trim();
    return locationName !== "the behive" && locationName !== "the beehive";
}

function addSessionRule(sessionRulesById: Map<string, string[]>, sessionId: string, rule: string) {
    const sessionRules = sessionRulesById.get(sessionId) || [];
    sessionRulesById.set(sessionId, [...sessionRules, rule]);
}

function getTravelSummariesBySessionId(sessions: Session[], locationsById: Map<string, Location>): Map<string, string> {
    const summariesBySessionId = new Map<string, string>();
    let previousSession: Session | undefined = undefined;

    [...sessions]
        .sort((sessionA, sessionB) => getSessionDateTime(sessionA) - getSessionDateTime(sessionB))
        .forEach((session) => {
            if (!previousSession) {
                summariesBySessionId.set(session.id, "First recorded check-in");
                previousSession = session;
                return;
            }

            const currentLocation = locationsById.get(session.locationId);
            const previousLocation = locationsById.get(previousSession.locationId);
            const distanceKm = getDistanceKm(previousLocation, currentLocation);
            const timeSinceLastCheckInMs = getSessionDateTime(session) - getSessionDateTime(previousSession);
            const distanceText = distanceKm === undefined ? "Unknown distance" : `${distanceKm.toFixed(1)} km`;
            const speedKmh = getTravelSpeedKmh(previousLocation, currentLocation, timeSinceLastCheckInMs);
            const speedText = speedKmh === undefined ? "Unknown km/h" : `${speedKmh.toFixed(1)} km/h`;

            summariesBySessionId.set(
                session.id,
                `${distanceText}, ${formatDuration(timeSinceLastCheckInMs)}, ${speedText}`
            );
            previousSession = session;
        });

    return summariesBySessionId;
}

function getSuspiciousSessionRules(sessions: Session[], locationsById: Map<string, Location>, user: User | undefined): Map<string, string[]> {
    const sessionRulesById = new Map<string, string[]>();
    const lastCheckInByLocation = new Map<string, Session>();
    const isStaffUser = user?.staff === true;
    let previousSession: Session | undefined = undefined;

    [...sessions]
        .sort((sessionA, sessionB) => getSessionDateTime(sessionA) - getSessionDateTime(sessionB))
        .forEach((session) => {
            const locationId = session.locationId;
            const checkInTime = getSessionDateTime(session);
            const previousSessionAtLocation = lastCheckInByLocation.get(locationId);
            const previousCheckInTimeAtLocation = previousSessionAtLocation ? getSessionDateTime(previousSessionAtLocation) : undefined;
            const currentLocation = locationsById.get(locationId);
            const previousLocation = previousSession ? locationsById.get(previousSession.locationId) : undefined;
            const previousCheckInTime = previousSession ? getSessionDateTime(previousSession) : undefined;

            const sessionDurationMs = getSessionDurationMs(session);
            if (
                previousCheckInTimeAtLocation !== undefined
                && checkInTime > 0
                && checkInTime - previousCheckInTimeAtLocation < ONE_HOUR_MS
                && sessionDurationMs !== undefined
                && sessionDurationMs > TEN_MINUTES_MS
            ) {
                addSessionRule(sessionRulesById, session.id, "Repeated check-in");
            }

            if (sessionDurationMs !== undefined && sessionDurationMs < TEN_MINUTES_MS) {
                addSessionRule(sessionRulesById, session.id, "Quick check-in");
            }

            if (!isStaffUser && previousLocation?.airport && currentLocation?.airport) {
                addSessionRule(sessionRulesById, session.id, "Multiple airports");
            }

            if (
                !isStaffUser
                && isAirsideAirportLocation(previousLocation)
                && currentLocation
                && !currentLocation.airport
                && previousCheckInTime !== undefined
                && checkInTime > 0
                && checkInTime - previousCheckInTime <= EIGHTEEN_HOURS_MS
            ) {
                addSessionRule(sessionRulesById, session.id, "Impossible check-in");
            }

            if (previousSession && previousSession.locationId !== locationId && previousCheckInTime !== undefined && checkInTime > 0) {
                const speedKmh = getTravelSpeedKmh(previousLocation, currentLocation, checkInTime - previousCheckInTime);
                if (speedKmh !== undefined && speedKmh > RAPID_TRAVEL_SPEED_KMH) {
                    addSessionRule(sessionRulesById, session.id, "Rapid travel");
                }
            }

            if (locationId && checkInTime > 0) {
                lastCheckInByLocation.set(locationId, session);
            }

            previousSession = session;
        });

    return sessionRulesById;
}

function SuperAdminUserAnalysis() {
    const [users, setUsers] = useState<User[]>([]);
    const [userSearch, setUserSearch] = useState<string>("");
    const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
    const [analysedUser, setAnalysedUser] = useState<User | undefined>(undefined);
    const [sessions, setSessions] = useState<Session[]>([]);
    const [analysisStartDate, setAnalysisStartDate] = useState<string>(DEFAULT_ANALYSIS_START_DATE);
    const [locationsById, setLocationsById] = useState<Map<string, Location>>(new Map<string, Location>());
    const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
    const [loadingSessions, setLoadingSessions] = useState<boolean>(false);
    const [showUserOptions, setShowUserOptions] = useState<boolean>(false);
    const [sessionFilter, setSessionFilter] = useState<string>("All");

    useEffect(() => {
        const loadUsers = async () => {
            setLoadingUsers(true);
            try {
                const usersSnapshot = await getDocs(query(collection(db, "users")));
                const loadedUsers: User[] = [];
                usersSnapshot.forEach((doc) => {
                    loadedUsers.push({ ...(doc.data() as User), id: doc.id });
                });
                loadedUsers.sort((userA, userB) => (userA.name || "").localeCompare(userB.name || ""));
                setUsers(loadedUsers);
            } finally {
                setLoadingUsers(false);
            }
        };

        loadUsers();
    }, []);

    useEffect(() => {
        const loadLocations = async () => {
            const locationsSnapshot = await getDocs(query(collection(db, "locations")));
            const loadedLocationsById = new Map<string, Location>();
            locationsSnapshot.forEach((doc) => {
                loadedLocationsById.set(doc.id, { ...(doc.data() as Location), id: doc.id });
            });
            setLocationsById(loadedLocationsById);
        };

        loadLocations();
    }, []);

    const filteredUsers = useMemo(() => {
        const search = userSearch.toLowerCase().trim();
        if (!search) {
            return [];
        }

        return users
            .filter((user) => (user.status || "").toUpperCase() !== "INACTIVE")
            .filter((user) => {
                const name = (user.name || "").toLowerCase();
                return name.includes(search);
            })
            .slice(0, 20);
    }, [users, userSearch]);

    const suspiciousSessionRules = useMemo(() => getSuspiciousSessionRules(sessions, locationsById, analysedUser), [sessions, locationsById, analysedUser]);
    const suspiciousSessionIds = useMemo(() => new Set(suspiciousSessionRules.keys()), [suspiciousSessionRules]);
    const uniqueCheckIns = useMemo(() => new Set(sessions.map(session => session.locationId).filter(Boolean)).size, [sessions]);
    const suspiciousPercentage = sessions.length > 0
        ? ((suspiciousSessionIds.size / sessions.length) * 100).toFixed(1)
        : "0.0";
    const sortedSessions = useMemo(
        () => [...sessions].sort((sessionA, sessionB) => getSessionDateTime(sessionB) - getSessionDateTime(sessionA)),
        [sessions]
    );
    const travelSummariesBySessionId = useMemo(
        () => getTravelSummariesBySessionId(sessions, locationsById),
        [sessions, locationsById]
    );
    const filteredSessions = useMemo(
        () => sessionFilter === "All"
            ? sortedSessions
            : sortedSessions.filter((session) => (suspiciousSessionRules.get(session.id) || []).includes(sessionFilter)),
        [sessionFilter, sortedSessions, suspiciousSessionRules]
    );
    const showStaffRuleMessage = analysedUser?.staff === true && STAFF_SKIPPED_RULES.includes(sessionFilter);

    const handleSelectUser = (user: User) => {
        setSelectedUser(user);
        setUserSearch(user.name || user.email || "");
        setShowUserOptions(false);
    };

    const handleAnalyseUser = async () => {
        if (!selectedUser) {
            return;
        }

        setLoadingSessions(true);
        setAnalysedUser(selectedUser);
        setSessionFilter("All");
        try {
            const startDate = new Date(`${analysisStartDate || DEFAULT_ANALYSIS_START_DATE}T00:00:00`);
            const sessionsQuery = query(
                collection(db, "sessions"),
                where("userId", "==", selectedUser.id),
                where("checkInTime", ">=", startDate),
                orderBy("checkInTime", "desc")
            );
            const sessionsSnapshot = await getDocs(sessionsQuery);
            const loadedSessions: Session[] = [];
            sessionsSnapshot.forEach((doc) => {
                loadedSessions.push({ ...(doc.data() as Session), id: doc.id });
            });
            setSessions(loadedSessions);
        } finally {
            setLoadingSessions(false);
        }
    };

    return (
        <>
            <Card className="mb-3">
                <Card.Header>
                    <b>User Selector</b>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={8}>
                            <div style={{ position: "relative" }}>
                                <Form.Control
                                    type="text"
                                    value={userSearch}
                                    placeholder={loadingUsers ? "Loading users..." : "Start typing a name"}
                                    disabled={loadingUsers}
                                    onChange={(event) => {
                                        setUserSearch(event.target.value);
                                        setSelectedUser(undefined);
                                        setShowUserOptions(true);
                                    }}
                                    onFocus={() => setShowUserOptions(true)}
                                    onBlur={() => setTimeout(() => setShowUserOptions(false), 150)}
                                />
                                {showUserOptions && filteredUsers.length > 0 && (
                                    <ListGroup
                                        style={{
                                            position: "absolute",
                                            top: "100%",
                                            left: 0,
                                            right: 0,
                                            zIndex: 1000,
                                            maxHeight: "260px",
                                            overflowY: "auto"
                                        }}
                                    >
                                        {filteredUsers.map((user) => (
                                            <ListGroup.Item
                                                key={user.id}
                                                action
                                                onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
                                                onClick={() => handleSelectUser(user)}
                                            >
                                                <div><b>{user.name || "Unnamed user"}</b></div>
                                                {user.email && <small className="text-muted">{user.email}</small>}
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                )}
                            </div>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col md={8}>
                            <Form.Control
                                type="date"
                                value={analysisStartDate}
                                onChange={(event) => setAnalysisStartDate(event.target.value)}
                            />
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col md={8}>
                            <Button
                                variant="primary"
                                className="w-100"
                                onClick={handleAnalyseUser}
                                disabled={!selectedUser || loadingSessions}
                            >
                                {loadingSessions ? "Analysing..." : "Analyse user"}
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {analysedUser && (
                <>
                    <Card className="mb-3">
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center">
                                <b>{analysedUser.name || analysedUser.email}</b>
                                {analysedUser.staff === true && <span className="badge badge-info">Staff</span>}
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {loadingSessions ? (
                                <div>Loading sessions...</div>
                            ) : (
                                <Row>
                                    <Col sm={6} lg={3} className="mb-2">
                                        <div className="text-muted">Check-ins</div>
                                        <h4>{sessions.length}</h4>
                                    </Col>
                                    <Col sm={6} lg={3} className="mb-2">
                                        <div className="text-muted">Unique check-ins</div>
                                        <h4>{uniqueCheckIns}</h4>
                                    </Col>
                                    <Col sm={6} lg={3} className="mb-2">
                                        <div className="text-muted">Suspicious check-ins</div>
                                        <h4>{suspiciousSessionIds.size}</h4>
                                    </Col>
                                    <Col sm={6} lg={3} className="mb-2">
                                        <div className="text-muted">Suspicious rate</div>
                                        <h4>{suspiciousPercentage}%</h4>
                                    </Col>
                                </Row>
                            )}
                        </Card.Body>
                    </Card>

                    <Card className="mb-3">
                        <Card.Header>
                            <b>Suspicous check-in rules</b>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-2">
                                <span className={getRuleBadgeClass("Repeated check-in")}>Repeated check-in</span>
                                <span className="ml-2">The same user checks into the same pub again less than 1 hour after their previous check-in at that pub, and stays for more than 10 minutes on the repeat check-in.</span>
                            </div>
                            <div>
                                <span className={getRuleBadgeClass("Multiple airports")}>Multiple airports</span>
                                <span className="ml-2">The user checks into an airport pub, then their next check-in is also at an airport pub.</span>
                            </div>
                            <div className="mt-2">
                                <span className={getRuleBadgeClass("Rapid travel")}>Rapid travel</span>
                                <span className="ml-2">The user checks into a different pub where the distance divided by time since their previous check-in is greater than 50 km/h.</span>
                            </div>
                            <div className="mt-2">
                                <span className={getRuleBadgeClass("Quick check-in")}>Quick check-in</span>
                                <span className="ml-2">The user checks in for less than 10 minutes.</span>
                            </div>
                            <div className="mt-2">
                                <span className={getRuleBadgeClass("Impossible check-in")}>Impossible check-in</span>
                                <span className="ml-2">The user checks into an air side airport pub, then checks into a non-airport pub within 18 hours.</span>
                            </div>
                        </Card.Body>
                    </Card>

                    <Card className="mb-3">
                        <Card.Header>
                            <div>
                                <div className="d-flex justify-content-between align-items-center">
                                    <b>User Activity</b>
                                    <span className="stats-card">{filteredSessions.length}</span>
                                </div>
                                <div className="mt-2">
                                    <Button
                                        variant={sessionFilter === "All" ? "primary" : "outline-primary"}
                                        size="sm"
                                        className="mr-2 my-1"
                                        onClick={() => setSessionFilter("All")}
                                    >
                                        All
                                    </Button>
                                    {SUSPICIOUS_RULES.map((rule) => (
                                        <Button
                                            key={rule}
                                            variant={sessionFilter === rule ? "primary" : "outline-primary"}
                                            size="sm"
                                            className="mr-2 my-1"
                                            onClick={() => setSessionFilter(rule)}
                                        >
                                            {rule}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body style={{ padding: 0 }}>
                            {loadingSessions ? (
                                <div className="p-3">Loading sessions...</div>
                            ) : showStaffRuleMessage ? (
                                <div className="p-3">
                                    This user is marked as staff, so <b>{sessionFilter}</b> is not tagged.
                                </div>
                            ) : filteredSessions.length === 0 ? (
                                <div className="p-3">No check-ins found</div>
                            ) : (
                                <ListGroup variant="flush">
                                    {filteredSessions.map((session) => {
                                        const sessionRules = suspiciousSessionRules.get(session.id) || [];
                                        return (
                                            <ListGroup.Item key={session.id}>
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        <Link to={`/location/${session.locationId}/comments`} className="text-decoration-none">
                                                            <b>{session.locationName || "Unknown pub"}</b>
                                                        </Link>
                                                        <div>
                                                            <small className="admin-session-detail-text">
                                                                {renderSessionTimeRange(session)}
                                                            </small>
                                                        </div>
                                                        <div>
                                                            <small className="admin-session-detail-text">
                                                                {travelSummariesBySessionId.get(session.id) || "Unknown distance, unknown time, unknown km/h"}
                                                            </small>
                                                        </div>
                                                    </div>
                                                    {sessionRules.length > 0 && (
                                                        <div className="ml-2 d-flex flex-column align-items-end">
                                                            {sessionRules.map((rule) => (
                                                                <span key={rule} className={`${getRuleBadgeClass(rule)} mb-1`}>{rule}</span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </ListGroup.Item>
                                        );
                                    })}
                                </ListGroup>
                            )}
                        </Card.Body>
                    </Card>
                </>
            )}
        </>
    );
}

export default SuperAdminUserAnalysis;
